import React, { useState, useEffect } from 'react';
import { UserCircle, Terminal, Copy, CheckCircle2, X, BookmarkPlus, Clock, Cpu, ChevronDown, ChevronUp, ThumbsUp, ThumbsDown } from 'lucide-react';
import { ConfirmationPrompt } from './ConfirmationPrompt';
import { Message, Theme } from '../types';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

// Robust clipboard copy with fallback for Linux/older browsers
async function copyToClipboardRobust(text: string): Promise<boolean> {
  try {
    // Try modern Clipboard API first
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }

    // Fallback for older browsers or Linux systems
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      return successful;
    } catch (err) {
      document.body.removeChild(textArea);
      return false;
    }
  } catch (err) {
    console.error('Failed to copy to clipboard:', err);
    return false;
  }
}
import { 
  Chart as ChartJS, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  RadialLinearScale,
  PieController,
  DoughnutController,
  BarController,
  LineController,
  LogarithmicScale
} from 'chart.js';
import { Bar, Line, Pie, Doughnut } from 'react-chartjs-2';
import ResizableTable from './ResizableTable';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  RadialLinearScale,
  PieController,
  DoughnutController,
  BarController,
  LineController,
  LogarithmicScale
);

interface ChatMessageProps {
  message: Message;
  theme: Theme;
  onPromoteToPrompt?: (content: string) => void;
  onVote?: (vote: 'up' | 'down') => void;
  hasDevAccess?: boolean;
}

interface ContentItem {
  type: string;
  text?: string;
  image_url?: {
    url: string;
  };
  html?: string;
  chart?: {
    type: string;
    data: any;
    options?: any;
  };
  image_data?: {
    format: string;  // 'png', 'jpeg', etc.
    base64: string;  // Base64 encoded image data
  };
  table?: {
    headers: string[];
    rows: string[][];
    caption?: string;
    theme?: 'modern' | 'minimal' | 'bordered' | 'striped';
  };
  user_confirm?: {
    question: string;
    action_type: string;
  };
  previouslySent?: boolean;
  lang?: string;
  headers?: string[];
  rows?: string[][];
  caption?: string;
  theme?: string;
}

// Helper function to format content (outside component to avoid React hook rules issues)
function formatContent(content: string | ContentItem[], message: Message) {
  // If content is already an array of content items
  if (Array.isArray(content)) {
    // Extract special content blocks first
    const codeBlocks: string[] = [];
    const specialBlocks: Array<{type: string, id: string, content: any}> = [];
    const specialBlockPlaceholders: {[key: string]: string} = {};
    
    // Process special content blocks
    content.forEach((item, index) => {
      if (item.type === 'code' && item.text) {
        // Handle code blocks
        const placeholder = `__CODE_BLOCK_${index}__`;
        codeBlocks.push(`\`\`\`${item.lang || ''}\n${item.text}\n\`\`\``);
        specialBlockPlaceholders[placeholder] = 'code';
      } 
      else if (item.type === 'html' && item.html) {
        // Handle HTML content
        const placeholder = `__HTML_BLOCK_${index}__`;
        specialBlocks.push({type: 'html', id: placeholder, content: item.html});
        specialBlockPlaceholders[placeholder] = 'html';
      }
      else if (item.type === 'chart' && item.chart) {
        // Handle chart data
        const placeholder = `__CHART_BLOCK_${index}__`;
        specialBlocks.push({type: 'chart', id: placeholder, content: item.chart});
        specialBlockPlaceholders[placeholder] = 'chart';
      }
      else if (item.type === 'image_data' && item.image_data?.base64) {
        // Handle inline base64 images
        const placeholder = `__IMAGE_DATA_${index}__`;
        const imgSrc = `data:image/${item.image_data.format || 'png'};base64,${item.image_data.base64}`;
        specialBlocks.push({type: 'image', id: placeholder, content: imgSrc});
        specialBlockPlaceholders[placeholder] = 'image';
      }
      else if (item.type === 'table') {
        // Handle table data - accept both item.table and direct properties
        //console.log("Processing table data:", JSON.stringify(item));
        const placeholder = `__TABLE_BLOCK_${index}__`;
        
        // Create a standardized table format regardless of input structure
        const tableData = {
          headers: item.table?.headers || item.headers || [],
          rows: item.table?.rows || item.rows || [],
          caption: item.table?.caption || item.caption || '',
          theme: item.table?.theme || item.theme || 'modern'
        };
        
        // Create sample data if needed for demo/test purposes
        if (tableData.headers.length > 0 && (!tableData.rows || tableData.rows.length === 0)) {
          console.log("Creating sample rows for demo table");
          tableData.rows = [
            ["Sample Data 1", "Sample Data 2", "Sample Data 3"],
            ["Row 2 Col 1", "Row 2 Col 2", "Row 2 Col 3"]
          ];
        }
        
        specialBlocks.push({type: 'table', id: placeholder, content: tableData});
        specialBlockPlaceholders[placeholder] = 'table';
      }
      else if (item.type === 'user_confirm' && item.user_confirm) {
        // Handle user confirmation requests - add extensive logging
        console.log("Processing user confirmation:", JSON.stringify(item));
        console.log("user_confirm properties:", Object.keys(item.user_confirm));
        console.log("Complete item structure:", JSON.stringify(item, null, 2));
        console.log("previouslySent value from item:", item.previouslySent);
        console.log("previouslySent type from item:", typeof item.previouslySent);
        
        const placeholder = `__USER_CONFIRM_${index}__`;
        
        // Create standardized confirmation data
        const confirmData = {
          question: item.user_confirm.question || "Would you like to proceed?",
          action_type: item.user_confirm.action_type || "default_action",
          previouslySent: item.previouslySent === true
        };
        
        console.log("Created confirmation data:", JSON.stringify(confirmData, null, 2));
        console.log("previouslySent value in confirmData:", confirmData.previouslySent);
        console.log("previouslySent type in confirmData:", typeof confirmData.previouslySent);
        
        specialBlocks.push({type: 'user_confirm', id: placeholder, content: confirmData});
        specialBlockPlaceholders[placeholder] = 'user_confirm';
        
        // Log the special block that was created
        console.log("Created special block:", JSON.stringify(specialBlocks[specialBlocks.length - 1], null, 2));
      }
    });

    // Get regular text parts
    const textParts = content
      .filter(item => item.type === 'text')
      .map(item => item.text)
      .filter(Boolean)
      .join('\n');
    
    // Get image URLs
    const imageUrls = content
      .filter(item => item.type === 'image_url')
      .map(item => item.image_url?.url)
      .filter(Boolean);

    // Combine text with placeholder references
    let result = textParts;
    
    // Add placeholders for special content blocks (without displaying the placeholder text)
    Object.keys(specialBlockPlaceholders).forEach(placeholder => {
      if (specialBlockPlaceholders[placeholder] !== 'code') {
        // For non-code blocks, add a special marker that we'll use to inject HTML later
        // Adding empty paragraph markers to ensure proper spacing without visible placeholder text
        result += `\n\n<!-- ${placeholder} -->\n\n`;
      }
    });
    
    // Replace code block placeholders with actual markdown code blocks
    codeBlocks.forEach((codeBlock, index) => {
      const placeholder = `__CODE_BLOCK_${index}__`;
      result = result.replace(placeholder, codeBlock);
    });
    
    // Store references to image URLs in message data
    if (imageUrls.length > 0) {
      // Store image URLs in the Message object
      message.images = imageUrls;
    }
    
    // Store special blocks for later rendering - ensure type safety
    if (specialBlocks.length > 0) {
      if (!message.specialBlocks) {
        message.specialBlocks = [];
      }
      // Clear existing blocks to avoid duplicates
      message.specialBlocks.length = 0;
      // Add the new blocks
      specialBlocks.forEach(block => message.specialBlocks?.push(block));
    }
    
    return result;
  }

  // Handle string content (as before)
  try {
    // First check if content is a string that contains code block objects
    if (typeof content === 'string') {
      // Check for possible code block JSON pattern
      if (content.includes('"type":"code"') && content.includes('"text"')) {
        try {
          const parsed = JSON.parse(content);
          if (Array.isArray(parsed)) {
            return formatContent(parsed, message);
          } else if (parsed && typeof parsed === 'object' && parsed.type === 'code' && parsed.text) {
            // Single code block object
            return `\`\`\`${parsed.lang || ''}\n${parsed.text}\n\`\`\``;
          }
        } catch (e) {
          // Not valid JSON, continue processing as regular content
        }
      }

      // Try to parse as structured content with possible HTML/images/tables
      if (content.includes('"type"') && (
          content.includes('"html"') ||
          content.includes('"image_data"') ||
          content.includes('"chart"') ||
          content.includes('"table"'))) {
        try {
          const parsed = JSON.parse(content);
          if (Array.isArray(parsed)) {
            return formatContent(parsed, message);
          } else if (parsed && typeof parsed === 'object') {
            // Single content item
            return formatContent([parsed], message);
          }
        } catch (e) {
          // Not valid JSON, try to repair malformed JSON from LLM
          const trimmed = content.trim();

          // Pattern 1a: Multiple objects starting with "type": and containing }, {
          // e.g., "type": "text", ...}, {"type": "table", ...}
          if (trimmed.startsWith('"type"') && trimmed.includes('}, {')) {
            try {
              // Try wrapping with [{ and ] (content already has closing braces)
              const repaired = '[{' + trimmed + ']';
              const parsed = JSON.parse(repaired);
              if (Array.isArray(parsed)) {
                console.warn('formatContent: Repaired malformed JSON (pattern 1a: multiple objects missing [{ and ])');
                return formatContent(parsed, message);
              }
            } catch (repairError) {
              // Try with }] ending if the content doesn't end with }
              try {
                const repairedAlt = '[{' + trimmed + '}]';
                const parsedAlt = JSON.parse(repairedAlt);
                if (Array.isArray(parsedAlt)) {
                  console.warn('formatContent: Repaired malformed JSON (pattern 1a alt: multiple objects missing [{ and }])');
                  return formatContent(parsedAlt, message);
                }
              } catch (repairError2) {
                // Both repairs failed, continue
              }
            }
          }

          // Pattern 1b: Single object starting with "type": (missing outer [{...}])
          if (trimmed.startsWith('"type"')) {
            try {
              // Try wrapping with [{ and }]
              const repaired = '[{' + trimmed + '}]';
              const parsed = JSON.parse(repaired);
              if (Array.isArray(parsed)) {
                console.warn('formatContent: Repaired malformed JSON (pattern 1b: single object missing outer brackets)');
                return formatContent(parsed, message);
              }
            } catch (repairError) {
              // Repair failed, continue processing as regular content
            }
          }

          // Pattern 2: Array items without outer brackets starting with {
          // e.g., {"type": "text", ...}, {"type": "table", ...}
          if (trimmed.startsWith('{') && trimmed.includes('}, {')) {
            try {
              const repaired = '[' + trimmed + ']';
              const parsed = JSON.parse(repaired);
              if (Array.isArray(parsed)) {
                console.warn('formatContent: Repaired malformed JSON array (pattern 2: missing outer brackets)');
                return formatContent(parsed, message);
              }
            } catch (repairError) {
              // Repair failed, continue processing as regular content
            }
          }
        }
      }

      // Try to parse as array of content items
      if (content.trim().startsWith('[')) {
        try {
          const contentArray = JSON.parse(content) as ContentItem[];
          return formatContent(contentArray, message);
        } catch (e) {
          // Not array JSON, process as regular content
        }
      }

      // Final fallback: detect malformed structured content without proper wrapping
      // Pattern: "type": "text" at start without leading [{
      const trimmed = content.trim();
      if (trimmed.startsWith('"type"') && trimmed.includes('"text"')) {
        // Check for multiple objects first
        if (trimmed.includes('}, {')) {
          try {
            const repaired = '[{' + trimmed + ']';
            const parsed = JSON.parse(repaired);
            if (Array.isArray(parsed)) {
              console.warn('formatContent: Repaired malformed JSON (final fallback - multiple objects)');
              return formatContent(parsed, message);
            }
          } catch (e) {
            // Try alternate repair
            try {
              const repairedAlt = '[{' + trimmed + '}]';
              const parsedAlt = JSON.parse(repairedAlt);
              if (Array.isArray(parsedAlt)) {
                console.warn('formatContent: Repaired malformed JSON (final fallback alt - multiple objects)');
                return formatContent(parsedAlt, message);
              }
            } catch (e2) {
              // Both repairs failed
            }
          }
        } else {
          // Single object
          try {
            const repaired = '[{' + trimmed + '}]';
            const parsed = JSON.parse(repaired);
            if (Array.isArray(parsed)) {
              console.warn('formatContent: Repaired malformed JSON (final fallback - single object)');
              return formatContent(parsed, message);
            }
          } catch (e) {
            // Repair failed, process as regular content
          }
        }
      }
    }
  } catch (e) {
    // Not JSON, process as regular content
  }
  
  return content as string;
}

// Simple helper functions to color different languages
function colorC(code: string): string {
  try {
    return code
      // Keywords
      .replace(/\b(int|void|char|return|if|else|for|while|do|switch|case|break|continue|struct|union|typedef|enum|const|static|extern|volatile|register|auto|signed|unsigned|long|short|float|double|sizeof)\b/g, 
        '<span class="code-keyword">$1</span>')
      // Preprocessor directives
      .replace(/(#\w+)/g, 
        '<span class="code-preprocessor">$1</span>')
      // Strings
      .replace(/"([^"]*)"/g, 
        '<span class="code-string">"$1"</span>')
      // Single chars
      .replace(/'([^']*)'/g, 
        '<span class="code-string">\'$1\'</span>')
      // Numbers
      .replace(/\b(\d+)\b/g, 
        '<span class="code-number">$1</span>')
      // Function names
      .replace(/\b([a-zA-Z_]\w*)\s*\(/g, 
        '<span class="code-function">$1</span>(')
      // Comments
      .replace(/\/\/([^\n]*)/g, 
        '<span class="code-comment">\/\/$1</span>');
  } catch (error) {
    console.error('Error coloring C code:', error);
    return code; // Return original code if any error occurs
  }
}

function colorJava(code: string): string {
  try {
    return code
      // Keywords
      .replace(/\b(public|private|protected|class|static|void|int|boolean|String|new|return|if|else|for|while|try|catch|throw|throws|import|package|extends|implements|instanceof|final|this|super)\b/g, 
        '<span class="code-keyword">$1</span>')
      // Strings
      .replace(/"([^"]*)"/g, 
        '<span class="code-string">"$1"</span>')
      // Numbers
      .replace(/\b(\d+)\b/g, 
        '<span class="code-number">$1</span>')
      // Function names
      .replace(/\b([a-zA-Z_]\w*)\s*\(/g, 
        '<span class="code-function">$1</span>(')
      // Comments
      .replace(/\/\/([^\n]*)/g, 
        '<span class="code-comment">\/\/$1</span>');
  } catch (error) {
    console.error('Error coloring Java code:', error);
    return code;
  }
}

function colorPython(code: string): string {
  try {
    return code
      // Keywords
      .replace(/\b(def|class|import|from|as|if|elif|else|for|while|try|except|finally|with|return|yield|raise|break|continue|pass|assert|lambda|None|True|False|and|or|not|in|is)\b/g, 
        '<span class="code-keyword">$1</span>')
      // Strings
      .replace(/("([^"]*)")|('([^']*)')/g, 
        '<span class="code-string">$1</span>')
      // Numbers
      .replace(/\b(\d+)\b/g, 
        '<span class="code-number">$1</span>')
      // Function names
      .replace(/\b(def\s+)([a-zA-Z_]\w*)/g, 
        '$1<span class="code-function">$2</span>')
      // Comments
      .replace(/#([^\n]*)/g, 
        '<span class="code-comment">#$1</span>');
  } catch (error) {
    console.error('Error coloring Python code:', error);
    return code;
  }
}

function colorJavaScript(code: string): string {
  try {
    return code
      // Keywords
      .replace(/\b(var|let|const|function|class|if|else|for|while|do|switch|case|break|continue|return|try|catch|finally|throw|typeof|instanceof|new|this|super|extends|import|export|default|null|undefined|true|false)\b/g, 
        '<span class="code-keyword">$1</span>')
      // Strings
      .replace(/("([^"]*)")|('([^']*)')|(`([^`]*)`)/g, 
        '<span class="code-string">$1</span>')
      // Numbers
      .replace(/\b(\d+)\b/g, 
        '<span class="code-number">$1</span>')
      // Function names
      .replace(/\b(function\s+)([a-zA-Z_]\w*)/g, 
        '$1<span class="code-function">$2</span>')
      // Method calls
      .replace(/\.([a-zA-Z_]\w*)\(/g, 
        '.<span class="code-function">$1</span>(')
      // Comments
      .replace(/\/\/([^\n]*)/g, 
        '<span class="code-comment">\/\/$1</span>');
  } catch (error) {
    console.error('Error coloring JavaScript code:', error);
    return code;
  }
}

// HTML content renderer component - separate component to avoid hook issues
function HtmlContent({ content }: { content: string }) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Use effect to safely initialize scripts after rendering
  React.useEffect(() => {
    if (containerRef.current) {
      setIsLoading(true);
      setError(null);
      
      // Create a secure div for HTML content
      const container = containerRef.current;
      
      // Clear any previous content
      container.innerHTML = '';
      
      // Create an iframe to sandbox the content
      // This is the most secure way to render external HTML/JS
      try {
        // For security, we'll create an iframe with limited permissions
        const iframe = document.createElement('iframe');
        iframe.style.width = '100%';
        iframe.style.border = 'none';
        iframe.style.backgroundColor = 'transparent';
        iframe.style.height = '500px'; // Default height
        iframe.setAttribute('sandbox', 'allow-scripts allow-popups allow-same-origin allow-modals');
        iframe.title = 'HTML Content';
        container.appendChild(iframe);
        
        // Function to process the content once iframe is loaded
        const processContent = () => {
          try {
            if (!iframe.contentWindow) {
              throw new Error('Cannot access iframe content window');
            }
            
            const doc = iframe.contentWindow.document;
            doc.open();
            
            // Write the content to the iframe
            doc.write(content);
            
            // Close the document
            doc.close();
            
            // Adjust iframe height to match content
            setTimeout(() => {
              if (iframe.contentWindow && iframe.contentWindow.document.body) {
                // Set min-height to avoid collapsing
                const minHeight = 300;
                const contentHeight = iframe.contentWindow.document.body.scrollHeight;
                iframe.style.height = `${Math.max(minHeight, contentHeight)}px`;
              }
              setIsLoading(false);
            }, 500);
            
            // Make Plotly available in the iframe
            if (content.includes('Plotly') && iframe.contentWindow) {
              // Create a script element for Plotly
              const script = document.createElement('script');
              script.src = 'https://cdn.plot.ly/plotly-latest.min.js';
              script.onload = () => {
                // Copy Plotly from parent window to iframe
                if (iframe.contentWindow && window.Plotly) {
                  iframe.contentWindow.Plotly = window.Plotly;
                }
              };
              iframe.contentWindow.document.head.appendChild(script);
            }
          } catch (err) {
            console.error('Error processing content in iframe:', err);
            setError('Error rendering HTML content');
            setIsLoading(false);
          }
        };
        
        // Wait for iframe to load before processing content
        iframe.onload = processContent;
        
        // Call process immediately as well (some browsers may not trigger onload)
        processContent();
        
      } catch (e) {
        console.error('Error setting up HTML content iframe:', e);
        setError('Failed to render HTML content');
        setIsLoading(false);
        
        // Fallback to showing the raw content
        container.innerHTML = `<div class="p-2 bg-red-100 text-red-800 rounded">
          Error rendering content
        </div>
        <pre class="mt-2 p-2 text-xs overflow-auto bg-gray-900 text-gray-300 rounded">${content}</pre>`;
      }
    }
  }, [content]);
  
  return (
    <div 
      className="my-4 p-4 bg-white dark:bg-gray-800 rounded-lg shadow"
      ref={containerRef}
    />
  );
}

// Image content renderer
function ImageContent({ src }: { src: string }) {
  return (
    <div className="my-4">
      <img 
        src={src} 
        alt="Generated content" 
        className="max-w-full h-auto rounded-lg"
        style={{ maxHeight: '400px' }}
      />
    </div>
  );
}

// Chart content renderer
class ChartErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    console.error('Error rendering chart:', error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="text-red-500 p-2 border border-red-300 rounded bg-red-50 dark:bg-red-900 dark:border-red-700">
          <p className="font-medium">Error rendering chart</p>
        </div>
      );
    }

    return this.props.children;
  }
}

function ChartContent({ data }: { data: any }) {
  const hasLabels = Array.isArray(data?.data?.labels);
  const hasDatasets = Array.isArray(data?.data?.datasets) && data.data.datasets.length > 0;
  const isValidChartData = hasLabels && hasDatasets;

  // Calculate chart height only once when component mounts or data changes
  useEffect(() => {
    if (hasLabels) {
      const datasetLength = data.data.labels.length || 5;
      const calculatedHeight = Math.max(300, datasetLength * 30);
      setChartHeight(calculatedHeight);
    }
  }, [data, hasLabels]);
  
  const chartRef = React.useRef<HTMLDivElement>(null);
  const [chartHeight, setChartHeight] = useState(300);
  const [copied, setCopied] = useState(false);
  const [showData, setShowData] = useState(false);
  const [downloading, setDownloading] = useState(false);
  
  // Copy chart data to clipboard
  const copyToClipboard = async () => {
    const chartData = JSON.stringify(data, null, 2);
    const success = await copyToClipboardRobust(chartData);

    if (success) {
      setCopied(true);
      // Reset copy icon after 2 seconds
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } else {
      console.error('Failed to copy chart data');
    }
  };
  
  // Download chart as image
  const downloadAsImage = async () => {
    try {
      if (!chartRef.current) return;
      
      setDownloading(true);
      
      // Get the chart canvas from the chart component
      const chartCanvas = chartRef.current.querySelector('canvas');
      if (!chartCanvas) {
        console.error('Chart canvas not found');
        setDownloading(false);
        return;
      }
      
      // Convert canvas to data URL
      const dataUrl = chartCanvas.toDataURL('image/png');
      
      // Create download link
      const downloadLink = document.createElement('a');
      const chartTitle = (data.type || 'chart').toLowerCase();
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      downloadLink.download = `${chartTitle}-${timestamp}.png`;
      downloadLink.href = dataUrl;
      
      // Trigger download
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      
      // Show success feedback with a toast-like message
      setDownloading(false);
      
      // Create and show a toast notification
      const toast = document.createElement('div');
      toast.className = 'fixed bottom-4 right-4 bg-green-500 text-white px-4 py-2 rounded shadow-lg z-50 animate-fade-in-out';
      toast.textContent = 'Chart downloaded as image';
      toast.style.transition = 'opacity 0.5s ease-in-out';
      toast.style.opacity = '0';
      document.body.appendChild(toast);
      
      // Fade in
      setTimeout(() => {
        toast.style.opacity = '1';
      }, 10);
      
      // Automatically remove after 3 seconds
      setTimeout(() => {
        toast.style.opacity = '0';
        setTimeout(() => {
          document.body.removeChild(toast);
        }, 500);
      }, 3000);
    } catch (err) {
      console.error('Failed to download chart as image:', err);
      setDownloading(false);
      
      // Show error toast notification
      const toast = document.createElement('div');
      toast.className = 'fixed bottom-4 right-4 bg-red-500 text-white px-4 py-2 rounded shadow-lg z-50 animate-fade-in-out';
      toast.textContent = 'Failed to download image';
      toast.style.transition = 'opacity 0.5s ease-in-out';
      toast.style.opacity = '0';
      document.body.appendChild(toast);
      
      // Fade in
      setTimeout(() => {
        toast.style.opacity = '1';
      }, 10);
      
      // Automatically remove after 3 seconds
      setTimeout(() => {
        toast.style.opacity = '0';
        setTimeout(() => {
          document.body.removeChild(toast);
        }, 500);
      }, 3000);
    }
  };
  
  if (!isValidChartData) {
    console.error('Error rendering chart: invalid data', data);
    return (
      <div className="my-4 p-4 bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="text-red-500 p-2 border border-red-300 rounded bg-red-50 dark:bg-red-900 dark:border-red-700">
          <p className="font-medium">Error rendering chart</p>
        </div>
      </div>
    );
  }

  // Make options more responsive
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    ...data.options
  };
  
  // Create the chart based on chart type
  const renderChart = () => {
    try {
      // Select chart type based on data.type
      const chartType = data.type?.toLowerCase() || 'bar';
      
      switch(chartType) {
        case 'bar':
          return <Bar data={data.data} options={chartOptions} />;
        case 'line':
          return <Line data={data.data} options={chartOptions} />;
        case 'pie':
          return <Pie data={data.data} options={chartOptions} />;
        case 'doughnut':
          return <Doughnut data={data.data} options={chartOptions} />;
        default:
          // Fallback to bar chart if type is unknown
          return <Bar data={data.data} options={chartOptions} />;
      }
    } catch (error) {
      console.error('Error rendering chart:', error);
      return (
        <div className="text-red-500 p-2 border border-red-300 rounded bg-red-50 dark:bg-red-900 dark:border-red-700">
          <p className="font-medium">Error rendering chart</p>
        </div>
      );
    }
  };

  return (
    <div className="my-4 p-4 bg-white dark:bg-gray-800 rounded-lg shadow relative">
      <div className="flex justify-between items-center mb-2">
        <p className="font-medium text-gray-700 dark:text-gray-300">
          {data.type || 'Bar'} Chart
        </p>
        <div className="flex gap-2">
          <button 
            onClick={() => setShowData(!showData)}
            className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 p-1 rounded"
            title={showData ? "Hide data" : "Show data"}
          >
            {showData ? 
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"></path><circle cx="12" cy="12" r="3"></circle></svg> :
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9.88 9.88a3 3 0 1 0 4.24 4.24"></path><path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"></path><path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61"></path><line x1="2" x2="22" y1="2" y2="22"></line></svg>
            }
          </button>
          <button 
            onClick={downloadAsImage}
            className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 p-1 rounded"
            title="Download chart as image"
            disabled={downloading}
          >
            {downloading ? 
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="animate-spin"><path d="M21 12a9 9 0 1 1-6.219-8.56"></path></svg> : 
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" x2="12" y1="15" y2="3"></line></svg>
            }
          </button>
          <button 
            onClick={copyToClipboard}
            className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 p-1 rounded"
            title="Copy chart data"
          >
            {copied ? 
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg> : 
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"></rect><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"></path></svg>
            }
          </button>
        </div>
      </div>
      
      <div ref={chartRef} style={{ height: `${chartHeight}px`, width: '100%' }}>
        <ChartErrorBoundary>{renderChart()}</ChartErrorBoundary>
      </div>
      
      {showData && (
        <div className="mt-4 border-t pt-4 dark:border-gray-700">
          <h4 className="text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Chart Data</h4>
          <pre className="text-xs overflow-auto p-2 bg-gray-100 dark:bg-gray-900 rounded max-h-60">
            {JSON.stringify(data, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

// Hardcoded test data for tables
const HARDCODED_TABLE_DATA = {
  headers: ["Name", "Age", "Salary", "Status", "Remarks"],
  rows: [
    ["John Doe", "32", "$85,000", "Active", "Senior Developer"],
    ["Jane Smith", "28", "$72,500", "Active", "UI/UX Designer"],
    ["Bob Johnson", "45", "$120,000", "On Leave", "Project Manager"],
    ["Alice Williams", "37", "$95,000", "Active", "Data Scientist"],
    ["Charlie Brown", "29", "$68,000", "Inactive", "Junior Developer"]
  ],
  caption: "Employee Information",
  theme: "modern"
};

// Table content renderer component
function TableContent({ data }: { data: any }) {
  const [copied, setCopied] = useState(false);
  
  // Extract properties with defaults
  const headers = data?.headers || [];
  const rows = data?.rows || [];
  const caption = data?.caption || '';
  const theme = data?.theme || 'modern';
  
  // Function to copy table data to clipboard in a human-readable format
  const copyTableToClipboard = async () => {
    try {
      let tableText = '';
      
      // Add caption if available
      if (caption) {
        tableText += `${caption}\n\n`;
      }
      
      // Add headers
      if (headers && headers.length > 0) {
        tableText += headers.join('\t') + '\n';
        tableText += headers.map(() => '---').join('\t') + '\n';
      }
      
      // Add rows
      if (rows && rows.length > 0) {
        rows.forEach(row => {
          tableText += row.join('\t') + '\n';
        });
      }
      
      const success = await copyToClipboardRobust(tableText);
      if (success) {
        setCopied(true);
        // Reset copy icon after 2 seconds
        setTimeout(() => {
          setCopied(false);
        }, 2000);
      } else {
        console.error('Failed to copy table');
      }
    } catch (err) {
      console.error('Failed to copy table data:', err);
    }
  };
  
  // Use ResizableTable component without individual copy button to avoid overlap
  // The message-level copy button will handle copying the entire message including tables
  return (
    <div className="relative">
      <ResizableTable 
        headers={headers} 
        rows={rows}
        caption={caption}
        theme={theme as any}
        wrapText={true}
      />
    </div>
  );
}

// Confirmation content handler component
function ConfirmationContent({ data, theme = 'light' }: { data: any, theme?: Theme }) {
  console.log('ConfirmationContent received data:', JSON.stringify(data, null, 2));
  console.log('previouslySent value:', data?.previouslySent);
  console.log('previouslySent type:', typeof data?.previouslySent);
  
  // Set initial state based only on previouslySent flag
  const initialResponseState = data?.previouslySent === true ? 'sent' : 'pending';
  console.log('Setting initialResponseState to:', initialResponseState);
  
  const [responseState, setResponseState] = useState<'pending' | 'sent' | 'error'>(initialResponseState);
  
  // Ensure we have a valid theme
  const safeTheme = theme || 'light';
  
  // Log data for debugging
  console.log('ConfirmationContent received theme:', safeTheme);
  console.log('ConfirmationContent initial state:', initialResponseState);
  console.log('previouslySent type:', typeof data?.previouslySent);
  console.log('previouslySent value:', data?.previouslySent);
  
  // Properly handle potentially undefined data
  if (!data) {
    console.error('ConfirmationContent received null/undefined data');
    data = {}; // Prevent further errors
  }
  
  // Use fallback values if data is invalid/missing properties
  const question = data?.question || "Would you like to proceed with this action?";
  const actionType = data?.action_type || "default_action";
  
  // Log the final values being used
  console.log('Final question:', question);
  console.log('Final actionType:', actionType);
  console.log('Final previouslySent:', data?.previouslySent);
  console.log('Final responseState:', responseState);
  
  const handleConfirm = async (e: React.MouseEvent) => {
    // Prevent event from bubbling up and potentially triggering form submissions
    e.preventDefault();
    e.stopPropagation();
    
    try {
      // Log action for debugging
      console.log(`Confirming action (YES) - Question: ${question}, Action type: ${actionType}`);
      
      const sessionId = localStorage.getItem('sessionId');
            
      // Send confirmation response
      await sendConfirmationResponse(question, actionType, 'yes');
      setResponseState('sent');
      
      // Dispatch a custom event for demo mode
      if (window.location.pathname === '/demo') {
        window.dispatchEvent(new CustomEvent('confirmation-response', {
          detail: {
            response: 'yes',
            actionType: actionType
          }
        }));
      }
    } catch (error) {
      console.error('Error sending confirmation:', error);
      setResponseState('error');
    }
  };
  
  const handleConfirmRemember = async (e: React.MouseEvent) => {
    // Prevent event from bubbling up
    e.preventDefault();
    e.stopPropagation();
    
    try {
      // Log action for debugging
      console.log(`Confirming action (YES_REMEMBER) - Question: ${question}, Action type: ${actionType}`);
      
      // Dispatch a custom event to tell Dashboard a confirmation was sent
      const sessionId = localStorage.getItem('sessionId');
      window.dispatchEvent(new CustomEvent('confirmation-sent', {
        detail: {
          question: question,
          actionType: actionType,
          response: 'yes_remember',
          sessionId: sessionId
        }
      }));
      
      // Send confirmation response with "remember" flag
      await sendConfirmationResponse(question, actionType, 'yes_remember');
      setResponseState('sent');
      
      // Dispatch a custom event for demo mode
      if (window.location.pathname === '/demo') {
        window.dispatchEvent(new CustomEvent('confirmation-response', {
          detail: {
            response: 'yes_remember',
            actionType: actionType
          }
        }));
      }
    } catch (error) {
      console.error('Error sending confirmation:', error);
      setResponseState('error');
    }
  };
  
  const handleDecline = async (e: React.MouseEvent) => {
    // Prevent event from bubbling up
    e.preventDefault();
    e.stopPropagation();
    
    try {
      // Log action for debugging
      console.log(`Declining action (NO) - Question: ${question}, Action type: ${actionType}`);
      
      // Dispatch a custom event to tell Dashboard a confirmation was sent
      const sessionId = localStorage.getItem('sessionId');
      window.dispatchEvent(new CustomEvent('confirmation-sent', {
        detail: {
          question: question,
          actionType: actionType,
          response: 'no',
          sessionId: sessionId
        }
      }));
      
      // Send decline response
      await sendConfirmationResponse(question, actionType, 'no');
      setResponseState('sent');
      
      // Dispatch a custom event for demo mode
      if (window.location.pathname === '/demo') {
        window.dispatchEvent(new CustomEvent('confirmation-response', {
          detail: {
            response: 'no',
            actionType: actionType
          }
        }));
      }
    } catch (error) {
      console.error('Error sending confirmation:', error);
      setResponseState('error');
    }
  };
  
  // Helper function to send confirmation response to backend
  const sendConfirmationResponse = async (question: string, actionType: string, response: 'yes' | 'yes_remember' | 'no') => {
    // Get the current active session ID from URL or localStorage
    const sessionId = localStorage.getItem('sessionId');
    
    // For demo/testing purposes: if we're on /demo route, just simulate success
    if (window.location.pathname === '/demo') {
      console.log(`Demo mode: Response "${response}" to question "${question}" for action "${actionType}"`);
      // Simulate API delay
      return new Promise(resolve => setTimeout(resolve, 500));
    }
    
    // Normal API call for production use
    if (!sessionId) {
      throw new Error('No active session found');
    }
    
    // Construct the message based on the response type
    let messageContent = '';
    switch (response) {
      case 'yes':
        messageContent = `\u200BYes, please proceed with the ${actionType} action.`;
        break;
      case 'yes_remember':
        messageContent = `\u200BYes, please proceed with the ${actionType} action. And please don't ask me again for this type of action in this conversation.`;
        break;
      case 'no':
        messageContent = `\u200BNo, I'll provide different instructions instead.`;
        break;
    }
    
    // Use the Dashboard.sendMessage function via custom event
    const sendMessageEvent = new CustomEvent('send-message', {
      detail: {
        message: messageContent,
        sessionId: sessionId
      }
    });
    window.dispatchEvent(sendMessageEvent);
    
    return { success: true };
  };
  
  // If response has been sent, show a confirmation message
  if (responseState === 'sent') {
    return (
      <div className={`p-3 my-2 rounded border ${safeTheme === 'dark' ? 'bg-gray-800 border-gray-700 text-gray-300' : 'bg-gray-50 border-gray-200 text-gray-700'}`}>
        <div className="flex items-center">
          <CheckCircle2 className="w-5 h-5 mr-2 text-green-500" />
          <p>Response sent</p>
        </div>
      </div>
    );
  }
  
  // If there was an error, show an error message
  if (responseState === 'error') {
    return (
      <div className={`p-3 my-2 rounded border ${safeTheme === 'dark' ? 'bg-red-900/50 border-red-800 text-red-200' : 'bg-red-50 border-red-200 text-red-700'}`}>
        <div className="flex items-center">
          <X className="w-5 h-5 mr-2 text-red-500" />
          <p>Error sending response. Please try again.</p>
        </div>
      </div>
    );
  }
  
  // Otherwise, show the confirmation prompt
  return (
    <ConfirmationPrompt
      question={question}
      actionType={actionType}
      onConfirm={handleConfirm}
      onConfirmRemember={handleConfirmRemember}
      onDecline={handleDecline}
      theme={safeTheme}
    />
  );
}

// Special content renderer component
function SpecialContent({ block, theme = 'light' }: { block: {type: string, id: string, content: any}, theme?: Theme }) {
  // Ensure we always have a valid theme, default to 'light' if undefined
  const safeTheme = theme || 'light';
  
  
  // Hide the placeholder ID from display by removing it from any visible element
  // We'll use a cleaner ID without the placeholder format for DOM purposes
  const cleanId = block.id.replace(/^__(.+)__$/, '$1').toLowerCase();
  
  // Return the appropriate component based on content type
  if (block.type === 'html') {
    return <HtmlContent content={block.content} />;
  } else if (block.type === 'image') {
    return <ImageContent src={block.content} />;
  } else if (block.type === 'chart') {
    return <ChartContent data={block.content} />;
  } else if (block.type === 'table') {
    
    // Check for data retrieval issue flag that was added during recovery
    if (block.content && block.content._dataRetrievalIssue) {
      console.log(`DEBUG SpecialContent: Found _dataRetrievalIssue flag, showing error message`);
      return (
        <div className="rounded-lg border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-800 p-4 my-4">
          <div className="flex items-start">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-500 mt-0.5 mr-3" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div>
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Data retrieval issue</h3>
              <p className="mt-1 text-sm text-red-700 dark:text-red-300">
                The actual table data for "{block.content.caption}" could not be retrieved. This can happen when reopening past conversations.
              </p>
              <p className="mt-2 text-xs text-red-700 dark:text-red-300">
                Try creating a new conversation to perform this query again.
              </p>
            </div>
          </div>
        </div>
      );
    }
    
    // Detect data issues in the table
    if (block.content && block.content.rows && block.content.rows.length > 0) {
      const rows = block.content.rows;
      
      // Check for the specific issue pattern: arrays with placeholder numeric values
      if (Array.isArray(rows[0]) && 
          rows[0].length > 1 && 
          rows[0].every(val => val === "0" || val === "1" || val === "_length")) {
        console.log(`DEBUG SpecialContent: Detected placeholder values in table rows`);
        
        // Show an error message instead of placeholder data
        return (
          <div className="rounded-lg border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-800 p-4 my-4">
            <div className="flex items-start">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-500 mt-0.5 mr-3" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div>
                <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Data retrieval issue</h3>
                <p className="mt-1 text-sm text-red-700 dark:text-red-300">
                  The actual table data for "{block.content.caption}" could not be retrieved. This can happen when reopening past conversations.
                </p>
                <p className="mt-2 text-xs text-red-700 dark:text-red-300">
                  Try creating a new conversation to perform this query again.
                </p>
              </div>
            </div>
          </div>
        );
      }
      
      // Check for object rows instead of arrays
      const firstRow = rows[0];
      if (typeof firstRow === 'object' && !Array.isArray(firstRow)) {
        console.log(`DEBUG SpecialContent: First row is object with keys:`, Object.keys(firstRow));
        
        // For object format conversion (different from placeholder values issue)
        // This is fixable by transforming the data format, so we do that
        const fixedContent = JSON.parse(JSON.stringify(block.content));
        
        // Convert each row object to an array based on headers
        fixedContent.rows = rows.map((row: any) => {
          if (Array.isArray(row)) return row;
          
          if (typeof row === 'object') {
            // Create an array with values in the correct header order
            return block.content.headers.map((_: any, i: number) => {
              // Try different key formats
              if (row[i.toString()] !== undefined) return row[i.toString()];
              if (row[i] !== undefined) return row[i];
              return '';
            });
          }
          
          return [String(row)];
        });
        
        console.log(`DEBUG SpecialContent: Fixed object row format:`, fixedContent.rows);
        return <TableContent data={fixedContent} />;
      }
    }
    
    return <TableContent data={block.content} />;
  } else if (block.type === 'user_confirm') {
    console.log('Rendering user_confirm block:', block);
    console.log('Block content for confirmation:', block.content);
    console.log('Using theme:', safeTheme);
    // Add fallback for missing or invalid data
    if (!block.content || typeof block.content !== 'object') {
      console.error('Invalid confirmation block content:', block.content);
      return (
        <div className="p-3 my-2 rounded border bg-red-100 text-red-800">
          <p>Error: Invalid confirmation data</p>
        </div>
      );
    }
    return <ConfirmationContent data={block.content} theme={safeTheme} />;
  }
  
  return null;
}

// Helper function to get step icon and label based on type
function getStepInfo(type: string): { icon: string; label: string; color: string } {
  const stepTypes: Record<string, { icon: string; label: string; color: string }> = {
    'user_query': { icon: '💬', label: 'User Query', color: 'border-l-blue-500' },
    'rag_query': { icon: '🔍', label: 'RAG Query', color: 'border-l-green-500' },
    'rag_result': { icon: '📚', label: 'RAG Results', color: 'border-l-green-400' },
    'rag_results': { icon: '📚', label: 'RAG Results', color: 'border-l-green-400' },
    'llm_request': { icon: '🤖', label: 'LLM Request', color: 'border-l-orange-500' },
    'llm_response': { icon: '💭', label: 'LLM Response', color: 'border-l-yellow-500' },
    'code_execute': { icon: '⚡', label: 'Fetching Data', color: 'border-l-blue-400' },
    'code_execution': { icon: '⚡', label: 'Fetching Data', color: 'border-l-blue-400' },
    'code_result': { icon: '📊', label: 'Data Retrieved', color: 'border-l-cyan-500' },
    'execution_result': { icon: '📊', label: 'Data Retrieved', color: 'border-l-cyan-500' },
    'final_answer': { icon: '✅', label: 'Final Answer', color: 'border-l-purple-500' },
    'finding_relevent_text': { icon: '🔍', label: 'Finding Relevant Text', color: 'border-l-green-500' },
    'storing_images': { icon: '🖼️', label: 'Storing Images', color: 'border-l-pink-500' },
    'storing_files': { icon: '📁', label: 'Storing Files', color: 'border-l-indigo-500' },
    'processing_files': { icon: '📄', label: 'Processing Files', color: 'border-l-gray-500' },
    'tidying_up': { icon: '🧹', label: 'Tidying Up', color: 'border-l-gray-400' },
    'finished': { icon: '✅', label: 'Finished', color: 'border-l-green-600' },
  };
  const normalizedType = type?.toLowerCase().replace(/\s+/g, '_') || 'unknown';
  return stepTypes[normalizedType] || { icon: '•', label: type || 'Processing', color: 'border-l-gray-400' };
}

// Format duration for display
function formatDuration(ms: number | undefined): string {
  if (ms === undefined || ms === null) return '';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

export function ChatMessage({ message, theme, onPromoteToPrompt, onVote, hasDevAccess = false }: ChatMessageProps) {
  // Default theme to light if not provided
  const safeTheme = theme || 'light';
  const [showFull, setShowFull] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showProcessingDetails, setShowProcessingDetails] = useState(false);
  const [expandedSteps, setExpandedSteps] = useState<Set<number>>(new Set());
  const categoryStats = React.useMemo(() => {
    if (!message.metadata?.processingSteps) return [];
    const stats = new Map<
      string,
      { label: string; count: number; totalMs: number }
    >();

    message.metadata.processingSteps.forEach((step, index) => {
      const prevStep =
        index > 0 ? message.metadata!.processingSteps![index - 1] : null;
      const durationMs =
        step.duration_ms ||
        (prevStep ? step.elapsed_ms - prevStep.elapsed_ms : step.elapsed_ms);

      if (durationMs === undefined || durationMs === null || Number.isNaN(durationMs)) {
        return;
      }

      const typeKey =
        (step.type || step.status || 'processing')
          .toString()
          .toLowerCase()
          .replace(/\s+/g, '_') || 'processing';
      const stepInfo = getStepInfo(step.type || step.status || 'processing');
      const entry = stats.get(typeKey) || {
        label: stepInfo.label,
        count: 0,
        totalMs: 0
      };

      entry.count += 1;
      entry.totalMs += durationMs;
      stats.set(typeKey, entry);
    });

    return Array.from(stats.entries())
      .map(([key, value]) => ({ key, ...value }))
      .sort((a, b) => b.totalMs - a.totalMs);
  }, [message.metadata?.processingSteps]);
  const totalCategoryMs = React.useMemo(() => {
    if (!categoryStats.length) return 0;
    return categoryStats.reduce((sum, category) => sum + category.totalMs, 0);
  }, [categoryStats]);
  const totalCategoryCount = React.useMemo(() => {
    if (!categoryStats.length) return 0;
    return categoryStats.reduce((sum, category) => sum + category.count, 0);
  }, [categoryStats]);

  // Toggle step expansion
  const toggleStep = (index: number) => {
    setExpandedSteps(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  // Close modal on Escape key
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showProcessingDetails) {
        setShowProcessingDetails(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showProcessingDetails]);

  // Function to copy message content to clipboard
  const copyToClipboard = async () => {
    try {
      let textToCopy = '';
      
      // Process structured content
      if (typeof message.content !== 'string' && Array.isArray(message.content)) {
        // Process each content item
        textToCopy = message.content.map(item => {
          // Handle table content
          if (item.type === 'table' && item.table) {
            const table = item.table;
            let tableText = '';
            
            // Add caption if available
            if (table.caption) {
              tableText += `${table.caption}\n\n`;
            }
            
            // Add headers
            if (table.headers && table.headers.length > 0) {
              tableText += table.headers.join('\t') + '\n';
              tableText += table.headers.map(() => '---').join('\t') + '\n';
            }
            
            // Add rows
            if (table.rows && table.rows.length > 0) {
              // Handle both array of arrays and array of objects with numeric keys
              table.rows.forEach(row => {
                if (Array.isArray(row)) {
                  tableText += row.join('\t') + '\n';
                } else if (typeof row === 'object') {
                  // Handle objects with numeric keys (like EC2 format)
                  const values = [];
                  for (let i = 0; i < table.headers.length; i++) {
                    values.push(row[i] || '');
                  }
                  tableText += values.join('\t') + '\n';
                }
              });
            }
            return tableText;
          } 
          // Handle text content
          else if (item.type === 'text' && item.text) {
            return item.text;
          }
          // Handle other types (fallback)
          else if (item.type && item[item.type]) {
            return `[${item.type} content]`;
          } 
          // Fallback for unknown formats
          else {
            return JSON.stringify(item);
          }
        }).join('\n\n');
      } 
      // Handle string content
      else {
        textToCopy = typeof message.content === 'string' 
          ? message.content 
          : JSON.stringify(message.content);
      }
        
      // Format text content
      textToCopy = textToCopy
        .replace(/```[\s\S]*?```/g, match => match) // Preserve code blocks
        .replace(/\*\*(.*?)\*\*/g, '$1')  // Remove bold
        .replace(/\*(.*?)\*/g, '$1')      // Remove italic
        .replace(/\[(.*?)\]\((.*?)\)/g, '$1 ($2)'); // Convert links to text with URL

      const success = await copyToClipboardRobust(textToCopy);
      if (success) {
        setCopied(true);
        // Reset copy icon after 2 seconds
        setTimeout(() => {
          setCopied(false);
        }, 2000);
      }
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  
  // Setup basic code styling
  useEffect(() => {
    // Add CSS for code blocks
    const style = document.createElement('style');
    style.textContent = `
      /* Code block styling */
      .markdown-content pre {
        background-color: #1e1e1e;
        color: #d4d4d4;
        padding: 16px;
        border-radius: 6px;
        overflow: auto;
        margin: 16px 0;
        font-family: 'Consolas', 'Courier New', monospace;
        white-space: pre-wrap;
        word-break: break-word;
      }
      
      /* Inline code */
      .markdown-content :not(pre) > code {
        background-color: rgba(55, 65, 81, 0.8);
        color: #f3f4f6;
        padding: 0.2em 0.4em;
        border-radius: 3px;
        font-family: monospace;
      }
      
      /* Code block content */
      .markdown-content pre code {
        display: block;
        width: 100%;
        text-wrap: wrap;
        font-size: 0.9em;
        line-height: 1.5;
      }
      
      /* Improved code block styling */
      .code-block {
        font-family: 'Consolas', 'Courier New', monospace;
        tab-size: 2;
      }
      
      /* Syntax highlighting classes */
      .code-keyword {
        color: #569CD6 !important;
        font-weight: bold;
      }
      .code-preprocessor {
        color: #C586C0 !important;
      }
      .code-string {
        color: #CE9178 !important;
      }
      .code-number {
        color: #B5CEA8 !important;
      }
      .code-function {
        color: #DCDCAA !important;
      }
      .code-comment {
        color: #6A9955 !important;
        font-style: italic;
      }
      
      /* Copy button styling */
      .message-copy-button {
        position: absolute;
        top: 12px;
        right: 12px;
        padding: 6px;
        background-color: rgba(0, 0, 0, 0.1);
        border-radius: 4px;
        cursor: pointer;
        opacity: 0;
        transition: opacity 0.2s ease-in-out;
      }
      
      .message-container {
        position: relative;
      }
      
      .message-container:hover .message-copy-button {
        opacity: 1;
      }
      
      /* Code block copy button */
      .code-copy-button {
        position: absolute;
        top: 8px;
        right: 8px;
        padding: 4px;
        background-color: rgba(255, 255, 255, 0.1);
        border-radius: 4px;
        cursor: pointer;
        opacity: 0;
        transition: opacity 0.2s ease-in-out;
      }
      
      pre {
        position: relative;
      }
      
      pre:hover .code-copy-button {
        opacity: 1;
      }
      
      /* Chart and HTML container */
      .special-content-container {
        margin: 16px 0;
        overflow: hidden;
        border-radius: 6px;
        box-shadow: 0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24);
      }
      
      /* Table container */
      .table-container {
        width: 100%;
        overflow-x: auto;
        border-radius: 8px;
      }
      
      /* Table styles for all themes */
      .table-container table {
        width: 100%;
        border-collapse: separate;
        border-spacing: 0;
        font-size: 14px;
      }
      
      /* Table header styling */
      .table-container th {
        white-space: nowrap;
        font-weight: 600;
        text-align: left;
      }
      
      /* Responsive table on small screens */
      @media (max-width: 640px) {
        .table-container {
          margin-left: -16px;
          margin-right: -16px;
          width: calc(100% + 32px);
          max-width: calc(100% + 32px);
          border-radius: 0;
        }
        
        .table-container table {
          border-radius: 0 !important;
        }
      }
    `;
    document.head.appendChild(style);
    
    // Simple custom renderer
    const renderer = new marked.Renderer();
    
    // Process code blocks with better approach
    renderer.code = function(code, language) {
      // Ensure code is a string and handle structured code objects
      let codeStr = '';
      let detectedLanguage = language || '';
      
      console.log('Code type:', typeof code);
      
      // Extract code from special OpenAI format objects
      if (typeof code === 'object' && code !== null) {
        try {
          // For objects like {type: "code", text: "actual code here", lang: "java"}
          if (code.type === 'code' && typeof code.text === 'string') {
            codeStr = code.text;
            // If language is specified in the object, use it
            if (code.lang && typeof code.lang === 'string') {
              detectedLanguage = code.lang;
              console.log('Using language from code object:', detectedLanguage);
            }
          } else {
            // Otherwise try to stringify the whole object
            codeStr = JSON.stringify(code, null, 2);
          }
        } catch (e) {
          console.error('Error processing code object:', e);
          codeStr = String(code);
        }
      } else if (typeof code === 'string') {
        codeStr = code;
      } else {
        codeStr = String(code || '');
      }
      
      console.log('Code content extracted:', codeStr.slice(0, 100) + '...');
      
      // Properly escape HTML characters to prevent XSS
      const escapeHtml = (unsafe: string) => {
        return unsafe
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;")
          .replace(/'/g, "&#039;");
      };
      
      // Detect language if not provided
      if (!detectedLanguage) {
        if (codeStr.indexOf('#include') >= 0 || codeStr.indexOf('int main') >= 0) {
          detectedLanguage = 'c';
        } else if (codeStr.indexOf('public class') >= 0 || codeStr.indexOf('System.out') >= 0) {
          detectedLanguage = 'java';
        } else if (codeStr.indexOf('def ') >= 0 || codeStr.indexOf('print(') >= 0) {
          detectedLanguage = 'python';
        } else if (codeStr.indexOf('function') >= 0 || codeStr.indexOf('console.log') >= 0) {
          detectedLanguage = 'javascript';
        }
      }
      
      // Apply syntax highlighting by adding tokens with classes
      const highlightSyntax = (code: string, lang: string) => {
        // First escape the HTML to prevent XSS
        let safeCode = escapeHtml(code);
        
        // Highlight tokens based on language
        if (lang === 'c' || lang === 'cpp') {
          // Keywords
          safeCode = safeCode.replace(/\b(int|void|char|return|if|else|for|while|do|switch|case|break|continue|struct|union|typedef|enum|const|static|extern|volatile|register|auto|signed|unsigned|long|short|float|double|sizeof)\b/g, 
            '<span class="code-keyword">$1</span>');
          // Preprocessor directives
          safeCode = safeCode.replace(/(#\w+)/g, 
            '<span class="code-preprocessor">$1</span>');
          // Strings
          safeCode = safeCode.replace(/(&quot;([^&quot;]*)&quot;)/g, 
            '<span class="code-string">$1</span>');
          // Single chars
          safeCode = safeCode.replace(/(&#039;([^&#039;]*)&#039;)/g, 
            '<span class="code-string">$1</span>');
          // Numbers
          safeCode = safeCode.replace(/\b(\d+)\b/g, 
            '<span class="code-number">$1</span>');
          // Function calls
          safeCode = safeCode.replace(/\b([a-zA-Z_]\w*)\s*\(/g, 
            '<span class="code-function">$1</span>(');
          // Comments
          safeCode = safeCode.replace(/\/\/([^\n]*)/g, 
            '<span class="code-comment">\/\/$1</span>');
        } else if (lang === 'java') {
          // Keywords
          safeCode = safeCode.replace(/\b(public|private|protected|class|static|void|int|boolean|String|new|return|if|else|for|while|try|catch|throw|throws|import|package|extends|implements|instanceof|final|this|super)\b/g, 
            '<span class="code-keyword">$1</span>');
          // Strings
          safeCode = safeCode.replace(/(&quot;([^&quot;]*)&quot;)/g, 
            '<span class="code-string">$1</span>');
          // Numbers
          safeCode = safeCode.replace(/\b(\d+)\b/g, 
            '<span class="code-number">$1</span>');
          // Function calls
          safeCode = safeCode.replace(/\b([a-zA-Z_]\w*)\s*\(/g, 
            '<span class="code-function">$1</span>(');
          // Comments
          safeCode = safeCode.replace(/\/\/([^\n]*)/g, 
            '<span class="code-comment">\/\/$1</span>');
        } else if (lang === 'python') {
          // Keywords
          safeCode = safeCode.replace(/\b(def|class|import|from|as|if|elif|else|for|while|try|except|finally|with|return|yield|raise|break|continue|pass|assert|lambda|None|True|False|and|or|not|in|is)\b/g, 
            '<span class="code-keyword">$1</span>');
          // Strings
          safeCode = safeCode.replace(/(&quot;([^&quot;]*)&quot;)|(&#039;([^&#039;]*)&#039;)/g, 
            '<span class="code-string">$1</span>');
          // Numbers
          safeCode = safeCode.replace(/\b(\d+)\b/g, 
            '<span class="code-number">$1</span>');
          // Function defs
          safeCode = safeCode.replace(/\b(def\s+)([a-zA-Z_]\w*)/g, 
            '$1<span class="code-function">$2</span>');
          // Comments
          safeCode = safeCode.replace(/#([^\n]*)/g, 
            '<span class="code-comment">#$1</span>');
        } else if (lang === 'javascript') {
          // Keywords
          safeCode = safeCode.replace(/\b(var|let|const|function|class|if|else|for|while|do|switch|case|break|continue|return|try|catch|finally|throw|typeof|instanceof|new|this|super|extends|import|export|default|null|undefined|true|false)\b/g, 
            '<span class="code-keyword">$1</span>');
          // Strings
          safeCode = safeCode.replace(/(&quot;([^&quot;]*)&quot;)|(&#039;([^&#039;]*)&#039;)/g, 
            '<span class="code-string">$1</span>');
          // Numbers
          safeCode = safeCode.replace(/\b(\d+)\b/g, 
            '<span class="code-number">$1</span>');
          // Function defs
          safeCode = safeCode.replace(/\b(function\s+)([a-zA-Z_]\w*)/g, 
            '$1<span class="code-function">$2</span>');
          // Method calls
          safeCode = safeCode.replace(/\.([a-zA-Z_]\w*)\(/g, 
            '.<span class="code-function">$1</span>(');
          // Comments
          safeCode = safeCode.replace(/\/\/([^\n]*)/g, 
            '<span class="code-comment">\/\/$1</span>');
        }
        
        return safeCode;
      };
      
      // Apply highlighting if a language is detected
      const highlightedCode = detectedLanguage ? 
        highlightSyntax(codeStr, detectedLanguage.toLowerCase()) : 
        escapeHtml(codeStr);
        
      // For our code blocks, we'll directly use DOM methods to create the elements
      // This helps bypass some of the issues with escaping and rendering
      
      // Create element in a string that will be properly parsed by the DOM
      const codeHtml = `
        <pre><code class="code-block ${detectedLanguage ? `language-${detectedLanguage.toLowerCase()}` : ''}">
          ${highlightedCode}
        </code></pre>
      `;
      
      return codeHtml;
    };
    
    // Configure marked with our custom renderer
    marked.setOptions({
      renderer: renderer,
      langPrefix: 'language-',
      gfm: true,       // Enable GitHub Flavored Markdown
      breaks: true,    // Convert line breaks to <br>
      pedantic: false, // Conform to markdown.pl
      smartLists: true,
      smartypants: false,
      // Identify code blocks more accurately
      highlight: function(code, lang) {
        return code; // We already handle highlighting in the renderer
      }
    });
    
    // Cleanup on unmount
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Function to create code copy buttons after render
  useEffect(() => {
    // Find all code blocks and add copy buttons
    const codeBlocks = document.querySelectorAll('pre');
    
    codeBlocks.forEach(block => {
      // Skip if already has a copy button
      if (block.querySelector('.code-copy-button')) return;
      
      // Create copy button
      const copyButton = document.createElement('button');
      copyButton.className = 'code-copy-button';
      copyButton.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>';
      copyButton.setAttribute('title', 'Copy code');
      
      // Add click handler
      copyButton.addEventListener('click', async (e) => {
        e.stopPropagation();

        // Get the code text
        const code = block.querySelector('code');
        if (!code) return;

        // Create text without HTML tags
        const textContent = code.textContent || '';

        // Copy to clipboard using robust method
        const success = await copyToClipboardRobust(textContent);
        if (success) {
          // Visual feedback
          copyButton.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><path d="m9 11 3 3L22 4"/></svg>';
          setTimeout(() => {
            copyButton.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>';
          }, 2000);
        } else {
          console.error('Failed to copy code');
        }
      });
      
      // Add button to code block
      block.appendChild(copyButton);
    });
  });

  const [isHovering, setIsHovering] = useState(false);
  const [isPromoted, setIsPromoted] = useState(false);
  
  const handlePromoteToPrompt = () => {
    if (onPromoteToPrompt && typeof message.content === 'string') {
      onPromoteToPrompt(message.content);
      setIsPromoted(true);
      
      // Reset after 2 seconds
      setTimeout(() => {
        setIsPromoted(false);
      }, 2000);
    }
  };
  
  return (
    <div 
      className={`py-6 ${message.role === 'assistant' ? 'bg-gray-50 dark:bg-gray-850 dark:bg-opacity-50' : ''}`}
      onMouseEnter={() => message.role === 'user' && setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <div className="max-w-3xl mx-auto flex gap-4 px-4 message-container relative">
        <div className="mt-1 flex-shrink-0">
          {message.role === 'user' ? (
            <UserCircle className="w-6 h-6 text-gray-600 dark:text-gray-300" />
          ) : message.role === 'assistant' ? (
            <img src="/icon_dark.svg" alt="Interpretos" className="w-6 h-6" />
          ) : (
            <Terminal className="w-6 h-6 text-purple-600 dark:text-purple-400" />
          )}
        </div>
        <div className="flex-1 min-w-0 overflow-hidden">
          {/* Promote to tile button for user messages */}
          {message.role === 'user' && isHovering && onPromoteToPrompt && (
            <button 
              className="promote-to-prompt-button absolute right-4 top-0 flex items-center justify-center p-1 rounded-md bg-blue-50 dark:bg-blue-900/20 text-blue-500 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-800/40 transition-colors"
              onClick={handlePromoteToPrompt}
              title="Save as prompt"
            >
              {isPromoted ? 
                <CheckCircle2 size={16} className="text-green-500" /> : 
                <BookmarkPlus size={16} />
              }
              <span className="ml-1 text-xs font-medium">Save as prompt</span>
            </button>
          )}
          
          {/* Copy button for entire message */}
          {message.role === 'assistant' && (
            <button 
              className="message-copy-button flex items-center justify-center" 
              onClick={copyToClipboard}
              title="Copy message"
            >
              {copied ? 
                <CheckCircle2 size={16} className="text-green-500" /> : 
                <Copy size={16} className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300" />
              }
            </button>
          )}
          <div className="prose dark:prose-invert max-w-none break-words markdown-content">
            {(() => {
              // Capture theme from outer scope to ensure it's available in the closure
              const currentTheme = safeTheme;
              
              try {
                // Format the content with our helpers
                
                const formattedContent = formatContent(message.content, message);
                
                // Parse the markdown first
                const parsedMarkdown = marked.parse(formattedContent);
                
                // Configure DOMPurify to properly preserve HTML in code blocks
                DOMPurify.addHook('beforeSanitizeElements', function(node) {
                  // Check if this is a code element inside a pre element (a code block)
                  if (node.tagName === 'CODE' && 
                      node.parentNode && 
                      node.parentNode.tagName === 'PRE') {
                    // Mark it for preservation
                    node.setAttribute('data-preserve-content', 'true');
                  }
                  return node;
                });
                
                DOMPurify.addHook('afterSanitizeAttributes', function(node) {
                  // If this is a code element we want to preserve
                  if (node.tagName === 'CODE' && node.hasAttribute('data-preserve-content')) {
                    // Remove our marker attribute
                    node.removeAttribute('data-preserve-content');
                  }
                });
                
                // Remove any visible placeholder markers from the HTML before sanitizing
                let cleanedMarkdown = parsedMarkdown.replace(/<!-- __[A-Z_]+\d+__ -->/g, '');
                cleanedMarkdown = cleanedMarkdown.replace(/__[A-Z_]+\d+__/g, ''); // Remove any unparsed placeholders
                
                // Use a more permissive sanitization for our specific syntax highlighting needs
                const sanitizedHtml = DOMPurify.sanitize(cleanedMarkdown, {
                  ADD_ATTR: ['class', 'style', 'id', 'onclick', 'onchange', 'onkeyup'],
                  ADD_TAGS: ['span', 'div', 'button'],
                  ALLOW_DATA_ATTR: true
                });
                
                return (
                  <>
                    <div dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
                    
                    {/* Render special content blocks if any */}
                    {message.specialBlocks && (
                      <div className="special-content-wrapper">
                        {message.specialBlocks.map((block, index) => (
                          <SpecialContent key={`${block.id}-${index}`} block={block} theme={currentTheme} />
                        ))}
                      </div>
                    )}
                  </>
                );
              } catch (error) {
                console.error('Error rendering markdown:', error);
                return (
                  <div className="p-4 bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 rounded">
                    <p>Error rendering content. Showing plain text:</p>
                    <pre className="mt-2 p-2 bg-gray-100 dark:bg-gray-800 rounded overflow-auto">
                      {typeof message.content === 'string' 
                        ? message.content 
                        : JSON.stringify(message.content, null, 2)}
                    </pre>
                  </div>
                );
              }
            })()}
          </div>
          {message.images && message.images.length > 0 && (
            <div className="mt-3 flex gap-2 flex-wrap">
              {message.images.map((image, index) => (
                <img
                  key={index}
                  src={image}
                  alt={`Uploaded content ${index + 1}`}
                  className="rounded-lg max-w-full h-auto"
                  style={{ maxWidth: 'min(300px, 100%)' }}
                />
              ))}
            </div>
          )}

          {/* Response metadata for assistant messages */}
          {message.role === 'assistant' && message.metadata && (
            <div className="mt-3 flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
              {message.metadata.model && (
                <span className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded font-mono">
                  <Cpu size={12} />
                  {message.metadata.model}
                </span>
              )}
              {message.metadata.responseTime !== undefined && (
                <span className="flex items-center gap-1">
                  <Clock size={12} />
                  {message.metadata.responseTime.toFixed(2)}s
                </span>
              )}
              {onVote && (
                <span className="flex items-center gap-1 ml-1">
                  <button
                    onClick={() => onVote('up')}
                    className={`p-1 rounded transition-colors ${
                      message.metadata?.vote === 'up'
                        ? 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/40'
                        : 'text-gray-400 hover:text-green-600 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20'
                    }`}
                    title="Upvote"
                    data-testid="vote-up"
                  >
                    <ThumbsUp size={14} />
                  </button>
                  <button
                    onClick={() => onVote('down')}
                    className={`p-1 rounded transition-colors ${
                      message.metadata?.vote === 'down'
                        ? 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/40'
                        : 'text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
                    }`}
                    title="Downvote"
                    data-testid="vote-down"
                  >
                    <ThumbsDown size={14} />
                  </button>
                </span>
              )}
              {hasDevAccess && message.metadata.processingSteps && message.metadata.processingSteps.length > 0 && (
                <button
                  onClick={() => setShowProcessingDetails(!showProcessingDetails)}
                  className="flex items-center gap-1 text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 underline"
                >
                  View Details
                  {showProcessingDetails ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                </button>
              )}
            </div>
          )}

          {/* Processing Details Modal */}
          {showProcessingDetails && message.metadata?.processingSteps && (
            <div
              className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
              onClick={(e) => {
                if (e.target === e.currentTarget) setShowProcessingDetails(false);
              }}
            >
              <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-3xl max-h-[80vh] flex flex-col">
                {/* Modal Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
                  <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200">Processing Details</h2>
                  <button
                    onClick={() => setShowProcessingDetails(false)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1"
                  >
                    <X size={20} />
                  </button>
                </div>

                {/* Modal Body */}
                <div className="flex-1 overflow-y-auto p-5">
                  {categoryStats.length > 0 && (
                    <div className="mb-5 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                      <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        Time Spend by Category
                      </div>
                      <div className="grid grid-cols-6 gap-2 px-4 py-2 text-xs text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700">
                        <span>Category</span>
                        <span className="text-right">Steps</span>
                        <span className="text-right">Time/Instance</span>
                        <span className="text-right">Total</span>
                        <span className="text-right">% Total</span>
                      </div>
                      <div className="divide-y divide-gray-200 dark:divide-gray-700">
                        {categoryStats.map((category) => {
                          const avgMs = category.count ? category.totalMs / category.count : 0;
                          const pct = totalCategoryMs ? (category.totalMs / totalCategoryMs) * 100 : 0;
                          return (
                            <div
                              key={`category-${category.key}`}
                              className="grid grid-cols-6 gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300"
                            >
                              <span className="font-medium">{category.label}</span>
                              <span className="text-right">{category.count}</span>
                              <span className="text-right">{formatDuration(avgMs)}</span>
                              <span className="text-right">{formatDuration(category.totalMs)}</span>
                              <span className="text-right">{pct.toFixed(1)}%</span>
                            </div>
                          );
                        })}
                      </div>
                      <div className="grid grid-cols-6 gap-2 px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-300 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                        <span>Total</span>
                        <span className="text-right">{totalCategoryCount}</span>
                        <span className="text-right">
                          {formatDuration(
                            totalCategoryCount ? totalCategoryMs / totalCategoryCount : 0
                          )}
                        </span>
                        <span className="text-right">{formatDuration(totalCategoryMs)}</span>
                        <span className="text-right">100%</span>
                      </div>
                    </div>
                  )}
                  <div className="space-y-3">
                    {message.metadata.processingSteps.map((step, index) => {
                      const stepInfo = getStepInfo(step.type || step.status);
                      const isExpanded = expandedSteps.has(index);
                      const prevStep = index > 0 ? message.metadata!.processingSteps![index - 1] : null;
                      const stepDuration = step.duration_ms || (prevStep ? step.elapsed_ms - prevStep.elapsed_ms : step.elapsed_ms);

                      return (
                        <div
                          key={`step-${index}`}
                          className={`border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden border-l-4 ${stepInfo.color}`}
                        >
                          {/* Step Header - Clickable */}
                          <div
                            className="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-750"
                            onClick={() => toggleStep(index)}
                          >
                            <div className="flex items-center gap-3">
                              <span className="text-lg">{stepInfo.icon}</span>
                              <span className="font-medium text-gray-700 dark:text-gray-300">{stepInfo.label}</span>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-sm font-semibold bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 px-2 py-0.5 rounded">
                                {formatDuration(stepDuration)}
                              </span>
                              <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                                @ {step.elapsed_ms}ms
                              </span>
                              {(step.content || step.description || step.metadata) && (
                                isExpanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />
                              )}
                            </div>
                          </div>

                          {/* Step Content - Expandable */}
                          {isExpanded && (step.content || step.description) && (
                            <div className="px-4 py-3 bg-gray-100/50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700">
                              <pre className="text-xs font-mono text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words max-h-48 overflow-y-auto">
                                {step.content || step.description}
                              </pre>
                            </div>
                          )}

                          {/* Step Metadata - Expandable */}
                          {isExpanded && step.metadata && Object.keys(step.metadata).length > 0 && (
                            <div className="px-4 py-2 bg-gray-200/50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-600 dark:text-gray-400">
                              {Object.entries(step.metadata).map(([key, value]) => (
                                <span key={key} className="mr-4">
                                  <strong>{key}:</strong> {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 flex justify-between items-center">
                  <span>
                    Total: {message.metadata.processingSteps.length} steps
                    {message.metadata.responseTime && ` • ${message.metadata.responseTime.toFixed(2)}s total`}
                  </span>
                  <button
                    onClick={() => setShowProcessingDetails(false)}
                    className="px-3 py-1.5 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded text-gray-700 dark:text-gray-300 text-sm"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}

          {message.role === 'system' && message.content.length > 1500 && (
            <button
              onClick={() => setShowFull(!showFull)}
              className="mt-2 text-xs text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300"
            >
              {showFull ? 'Show less' : 'Show full message'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
